/* ============================================================
   Общие элементы админ-панели: карточки, пилюли статусов,
   обложки из hue, кнопки, загрузка картинок.
   ============================================================ */
import { useRef, useState } from 'react';
import { Ic } from '../icons';
import { useFedContent, uploadUrl } from '../content';
import { useAppStore } from '../../store';

export const ST_MAP = {
  done: { color: 'var(--fed-fg-muted)', bg: 'var(--fed-surface-3)', label: 'Завершён' },
  live: { color: 'var(--fed-ok)', bg: 'rgba(67,217,106,.12)', label: 'Идёт' },
  soon: { color: 'var(--fed-gold)', bg: 'rgba(230,183,60,.12)', label: 'Анонс' },
  registration: { color: 'var(--fed-ok)', bg: 'rgba(67,217,106,.12)', label: 'Регистрация открыта' },
  closed: { color: 'var(--fed-accent-bright)', bg: 'rgba(225,18,23,.12)', label: 'Регистрация закрыта' },
  new: { color: 'var(--fed-info)', bg: 'rgba(59,165,217,.14)', label: 'Новая' },
  approved: { color: 'var(--fed-ok)', bg: 'rgba(67,217,106,.12)', label: 'Принята' },
  rejected: { color: 'var(--fed-accent-bright)', bg: 'rgba(225,18,23,.12)', label: 'Отклонена' },
  work: { color: 'var(--fed-gold)', bg: 'rgba(230,183,60,.12)', label: 'В работе' },
};

export function StPill({ st, label }) {
  const s = ST_MAP[st] || ST_MAP.new;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full font-mono uppercase" style={{ padding: '4px 10px', fontSize: 10, letterSpacing: '.1em', color: s.color, background: s.bg }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color }} />
      {label || s.label}
    </span>
  );
}

export function AdmCard({ children, className = '', style }) {
  return (
    <div className={`fed-admin-card rounded-2xl border border-line ${className}`} style={{ background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))', ...style }}>
      {children}
    </div>
  );
}

export const hueCover = (hue) => `linear-gradient(150deg,hsl(${hue} 55% 24%),hsl(${hue} 40% 12%))`;

export function CoverSwatch({ hue, size = 36, className = '' }) {
  return <span className={`rounded-lg flex-shrink-0 border border-line-2 ${className}`} style={{ width: size, height: size * 0.8, display: 'inline-block', background: hueCover(hue ?? 8) }} />;
}

export function AdmPrimaryBtn({ children, onClick, icon = 'plus', className = '', type = 'button' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`h-10 px-4 border-none rounded-[10px] cursor-pointer font-display font-bold uppercase text-white inline-flex items-center gap-2 ${className}`}
      style={{ fontSize: 13, letterSpacing: '.04em', background: 'linear-gradient(120deg,var(--fed-accent-bright),var(--fed-accent-deep))', boxShadow: '0 10px 26px -12px var(--fed-accent-glow)' }}
    >
      {icon ? <Ic name={icon} size={15} sw={2.4} /> : null}
      {children}
    </button>
  );
}

export function AdmIconBtn({ name, onClick, title, tint }) {
  const tintStyle = tint === 'ok'
    ? { color: 'var(--fed-ok)', borderColor: 'rgba(67,217,106,.4)', background: 'rgba(67,217,106,.08)' }
    : tint === 'danger'
      ? { color: 'var(--fed-accent-bright)', borderColor: 'var(--fed-line-accent)', background: 'rgba(225,18,23,.08)' }
      : tint === 'gold'
        ? { color: 'var(--fed-gold)', borderColor: 'rgba(230,183,60,.4)', background: 'rgba(230,183,60,.08)' }
        : {};
  return (
    <button className="fed-iconbtn" onClick={onClick} title={title} style={tintStyle}>
      <Ic name={name} size={16} />
    </button>
  );
}

/** «Смотреть страницу» — публичный адрес витрины в новой вкладке.
    route: 'article/n1', 'tournament/cs2-open-2026', 'project/i5'… */
export function ViewOnSiteBtn({ route, title = 'Смотреть на сайте' }) {
  return (
    <a
      href={`/#${route}`}
      target="_blank"
      rel="noopener noreferrer"
      className="fed-iconbtn no-underline"
      title={title}
      onClick={(e) => e.stopPropagation()}
    >
      <Ic name="external" size={15} />
    </a>
  );
}

/** Загрузка картинки на сервер (логотип партнёра, фото сотрудника, обложка
    поста). Хранится путь вида 'uploads/content_….png' — его и отдаём наверх. */
export function AdmImagePicker({ label, value, onChange, hint, preview = { w: 56, h: 56, radius: 12 } }) {
  const fire = useAppStore((s) => s.fire);
  const uploadImage = useFedContent((s) => s.uploadImage);
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // чтобы повторный выбор того же файла снова сработал
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadImage(file));
    } catch (err) {
      fire && fire(err.message || 'Не удалось загрузить файл', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-[7px]" style={{ gridColumn: '1 / -1' }}>
      {label ? <span className="fed-label">{label}</span> : null}
      <div className="flex items-center gap-3">
        <div
          className="flex items-center justify-center border border-line flex-shrink-0 overflow-hidden"
          style={{ width: preview.w, height: preview.h, borderRadius: preview.radius, background: 'var(--fed-surface-3)' }}
        >
          {value
            ? <img src={uploadUrl(value)} alt="" className="w-full h-full" style={{ objectFit: 'cover' }} />
            : <Ic name="image" size={20} color="var(--fed-fg-faint)" />}
        </div>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={pick} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="fed-btn-ghost h-9 px-3.5 rounded-[10px]" style={{ fontSize: 12.5, opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Загружаем…' : value ? 'Заменить' : 'Загрузить'}
        </button>
        {value && !busy && (
          <button type="button" onClick={() => onChange('')} className="fed-btn-ghost h-9 px-3.5 rounded-[10px]" style={{ fontSize: 12.5 }}>
            Убрать
          </button>
        )}
      </div>
      {hint ? <span className="text-fg-faint" style={{ fontSize: 11 }}>{hint}</span> : null}
    </div>
  );
}

export function EmptyRow({ children, colSpan }) {
  return (
    <tr>
      <td colSpan={colSpan} className="text-center text-fg-faint" style={{ padding: '28px 16px', fontSize: 13 }}>
        {children}
      </td>
    </tr>
  );
}
